import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import ReactECharts from 'echarts-for-react';
import { CSSTransition } from 'react-transition-group';

import { ButtonWithEye, EmptyAiresState } from '@/components';
import { prepareLineChartOptions } from '@/utils';

import { LineChartProps } from './types';

import styles from './styles.module.scss';

export const LineChart: FC<LineChartProps> = memo(({ isEmpty, label, series, medianMinValue, medianMaxValue }) => {
  const [isLegendVisible, setIsLegendVisible] = useState(true);

  const chartRef = useRef(null);
  const options = useMemo(
    () => prepareLineChartOptions(medianMinValue, medianMaxValue, series),
    [medianMinValue, medianMaxValue, series],
  );

  const handleLegendClick = useCallback(() => {
    setIsLegendVisible((isLegendVisible) => !isLegendVisible);
  }, []);

  return (
    <div className={styles.chart}>
      <div className={styles.chart__header}>
        <div className={styles.chart__header__item}>
          <h4 className={styles.chart__header__item__title}>{label}</h4>
        </div>

        {!isEmpty && (
          <div className={styles.chart__header__item}>
            <div className={styles.chart__header__item__legend}>
              <ButtonWithEye
                title={isLegendVisible ? 'Hide Legend' : 'Show Legend'}
                isOpen={isLegendVisible}
                onClick={handleLegendClick}
              />
            </div>
          </div>
        )}
      </div>

      {isEmpty ? (
        <EmptyAiresState
          styleClass={styles.emptyState}
          showChartIcon
          title="Time Series Chart"
          text="As you upload more CDFI-related data over time, a chart will display your credit union's evolution regarding the percentage of loans and loan amounts that qualify toward CDFI requirements"
        />
      ) : (
        <>
          <CSSTransition in={isLegendVisible} nodeRef={chartRef} timeout={500} classNames="my-node" unmountOnExit>
            <div className={styles.legend}>
              <div className={styles.legend__wrapper}>
                <div className={styles.lel}>
                  <h4 className={styles.legend__wrapper__item__title}>Number of Loans</h4>
                  <div className={styles.legend__wrapper__item}>
                    <span
                      className={clsx(
                        styles.legend__wrapper__item__line,
                        styles.legend__wrapper__item__line__dash,
                        styles.legend__wrapper__item__line__grey,
                      )}
                    />
                    <span className={styles.legend__wrapper__item__text}>
                      Level needed to meet qualification requirements
                    </span>
                  </div>

                  <div className={styles.legend__wrapper__item}>
                    <span
                      className={clsx(
                        styles.legend__wrapper__item__line,
                        styles.legend__wrapper__item__line__solid,
                        styles.legend__wrapper__item__line__red,
                      )}
                    />
                    <span className={styles.legend__wrapper__item__text}>Status not met</span>
                  </div>

                  <div className={styles.legend__wrapper__item}>
                    <span
                      className={clsx(
                        styles.legend__wrapper__item__line,
                        styles.legend__wrapper__item__line__solid,
                        styles.legend__wrapper__item__line__green,
                      )}
                    />
                    <span className={styles.legend__wrapper__item__text}>Pre-Qualified</span>
                  </div>
                </div>

                <div className={styles.lel}>
                  <h4 className={styles.legend__wrapper__item__title}>Dollar amount</h4>
                  <div className={styles.legend__wrapper__item}>
                    <span
                      className={clsx(
                        styles.legend__wrapper__item__line,
                        styles.legend__wrapper__item__line__dash,
                        styles.legend__wrapper__item__line__grey,
                      )}
                    />
                    <span className={styles.legend__wrapper__item__text}>
                      Level needed to meet qualification requirements
                    </span>
                  </div>

                  <div className={styles.legend__wrapper__item}>
                    <span
                      className={clsx(
                        styles.legend__wrapper__item__line,
                        styles.legend__wrapper__item__line__dash,
                        styles.legend__wrapper__item__line__red,
                      )}
                    />
                    <span className={styles.legend__wrapper__item__text}>Status not met</span>
                  </div>

                  <div className={styles.legend__wrapper__item}>
                    <span
                      className={clsx(
                        styles.legend__wrapper__item__line,
                        styles.legend__wrapper__item__line__dash,
                        styles.legend__wrapper__item__line__green,
                      )}
                    />
                    <span className={styles.legend__wrapper__item__text}>Pre-Qualified</span>
                  </div>
                </div>
              </div>
            </div>
          </CSSTransition>

          <div className={styles.chart__main}>
            <div style={{ flex: 'auto' }}>
              <ReactECharts
                option={options}
                notMerge
                lazyUpdate
                style={{ marginTop: '4px', height: '100%' }}
                opts={{ renderer: 'canvas' }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
});
